import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logoutUser } from "../../../Redux/slice/features/authSlice";
import { REACT_APP_API_URL } from "../../../env";
import {
  getLockoutStatus,
  registerFailure,
  clearLockout,
  formatRemainingMinutes,
} from "./lockout";

const PasswordReset = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const { hrmsUser } = useSelector((state) => state.auth);
  const empCode = hrmsUser?.employee_id;
  const identifier = useMemo(
    () => String(empCode || "").trim().toLowerCase(),
    [empCode]
  );
  const [lockoutInfo, setLockoutInfo] = useState(() =>
    getLockoutStatus("hrms-reset", "")
  );
  const GENERIC_RESET_ERROR = "Invalid details provided.";

  useEffect(() => {
    setLockoutInfo(getLockoutStatus("hrms-reset", identifier));
  }, [identifier]);

  const validatePasswordPolicy = (password, uid) => {
    const hasMinLength = password.length >= 8;
    const hasLowercase = /[a-z]/.test(password);
    const hasUppercase = /[A-Z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const isSameAsUserId = uid && password.toLowerCase() === String(uid).toLowerCase();

    return hasMinLength && hasLowercase && hasUppercase && hasDigit && hasSpecial && !isSameAsUserId;
  };

  const preventCopyPasteAndShortcuts = (e) => {
    const blockedKey = ["c", "v", "x"].includes(e.key?.toLowerCase());
    if ((e.ctrlKey || e.metaKey) && blockedKey) {
      e.preventDefault();
    }
  };

  // Allow access from auth flow without requiring an HRMS session.

  const handleSubmit = async (e) => {
    e.preventDefault();

    const lockoutStatus = getLockoutStatus("hrms-reset", identifier);
    if (lockoutStatus.isLocked) {
      alert(
        `Too many failed attempts. Try again in ${formatRemainingMinutes(
          lockoutStatus.remainingMs
        )}.`
      );
      return;
    }

    if (!oldPassword || !newPassword || !confirmPassword) {
      alert("Please fill in all fields.");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("New password and confirm password do not match!");
      return;
    }

    if (!validatePasswordPolicy(newPassword, empCode)) {
      alert(
        "Password must be at least 8 characters and include at least one lowercase letter, one uppercase letter, one number, one special character, and must not be the same as your user ID."
      );
      return;
    }

    try {
      setLoading(true);

      const res = await axios.post(
        `${REACT_APP_API_URL}api/auth/hrms/HRMS-reset-password`,
        {
          empCode: empCode,
          currentPassword: oldPassword,
          newPassword: newPassword,
        }
      );

      if (res.status === 200) {
        alert("Password reset successfully!");
        clearLockout("hrms-reset", identifier);
        setLockoutInfo(getLockoutStatus("hrms-reset", identifier));

        // Clear session and Redux state
        sessionStorage.removeItem("hrmsLogin");
        dispatch(logoutUser());

        navigate("/password-reset", { replace: true });

        return;
      }

      alert(GENERIC_RESET_ERROR);
      const updated = registerFailure("hrms-reset", identifier);
      setLockoutInfo(updated);
    } catch (err) {
      console.error("Error during password reset:", err);
      const updated = registerFailure("hrms-reset", identifier);
      setLockoutInfo(updated);
      if (updated.isLocked) {
        alert(
          `Too many failed attempts. Account locked for ${formatRemainingMinutes(
            updated.remainingMs
          )}.`
        );
      } else {
        alert(
          `${GENERIC_RESET_ERROR}. ${updated.attemptsLeft} attempt${updated.attemptsLeft === 1 ? "" : "s"
          } left.`
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-xl-5 px-4 auth-body">
      <form onSubmit={handleSubmit}>
        <ul className="row g-3 list-unstyled li_animate">

          <li className="col-12">
            <label className="form-label">Current Password</label>
            <div className="input-group">
              <input
                type={showOld ? "text" : "password"}
                className="form-control form-control-lg"
                placeholder="Enter current password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onKeyDown={preventCopyPasteAndShortcuts}
                required
              />
              <button
                type="button"
                className="btn btn-light border shadow-none"
                onClick={() => setShowOld(!showOld)}
              >
                {showOld ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
              </button>
            </div>
          </li>

          <li className="col-12">
            <label className="form-label">New Password</label>
            <div className="input-group">
              <input
                type={showNew ? "text" : "password"}
                className="form-control form-control-lg"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onKeyDown={preventCopyPasteAndShortcuts}
                required
              />
              <button
                type="button"
                className="btn btn-light border shadow-none"
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
              </button>
            </div>
          </li>

          <li className="col-12">
            <label className="form-label">Confirm New Password</label>
            <div className="input-group">
              <input
                type={showConfirm ? "text" : "password"}
                className="form-control form-control-lg"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                onCopy={(e) => e.preventDefault()}
                onCut={(e) => e.preventDefault()}
                onPaste={(e) => e.preventDefault()}
                onKeyDown={preventCopyPasteAndShortcuts}
                required
              />
              <button
                type="button"
                className="btn btn-light border shadow-none"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? <i className="bi bi-eye-slash"></i> : <i className="bi bi-eye"></i>}
              </button>
            </div>
          </li>

          <li className="col-12 my-lg-4">
            <p className="small text-muted mb-2">
              Password must be at least 8 characters and include uppercase, lowercase, number, special character, and must not match your user ID.
            </p>
            <button
              type="submit"
              className="btn btn-lg w-100 btn-primary text-uppercase mb-2"
              disabled={loading || lockoutInfo.isLocked}
            >
              {loading ? "Please wait..." : "Reset Password"}
            </button>
            {identifier && !lockoutInfo.isLocked && (
              <div className="text-muted small text-center">
                {lockoutInfo.attemptsLeft} attempt{lockoutInfo.attemptsLeft === 1 ? "" : "s"} left
              </div>
            )}
            {lockoutInfo.isLocked && (
              <div className="alert alert-warning mt-2 text-center">
                Account locked. Try again in {formatRemainingMinutes(lockoutInfo.remainingMs)}.
              </div>
            )}
          </li>

          <li className="col-12 text-center">
            <span className="text-muted">
              <Link to="/">Back to Homepage</Link>
            </span>
          </li>
        </ul>
      </form>
    </div>
  );
};

export default PasswordReset;